import { sleep } from "./schedule";

export async function retry<T>(
  fn: (attempt: number) => Promise<T>,
  max: number,
  delay?: Temporal.Duration | ((count: number) => Temporal.Duration),
): Promise<T> {
  for (let count = 1; count < max; count++) {
    try {
      return await fn(count);
    } catch {
      //
    }
    if (delay) {
      await sleep(typeof delay === "function" ? delay(count) : delay);
    }
  }
  return await fn(max);
}

export interface ExponentialBackoffOptions {
  maxAttempts: number;
  baseDelay: Temporal.Duration;
  maxDelay: Temporal.Duration;
  jitterFactor?: number;
  shouldRetry?: (error: any) => boolean;
}

export async function exponentialBackoffRetry<T>(
  fn: () => Promise<T>,
  options: ExponentialBackoffOptions,
): Promise<T> {
  const {
    maxAttempts,
    baseDelay = Temporal.Duration.from({ milliseconds: 100 }),
    maxDelay = Temporal.Duration.from({ seconds: 5 }),
    jitterFactor = 0.1,
  } = options;

  for (let attempt = 1; attempt < maxAttempts; attempt++) {
    try {
      return await fn();
    } catch (error) {
      if (options.shouldRetry && !options.shouldRetry(error)) {
        throw error;
      }
      const exponentialDelay = baseDelay.total("milliseconds") * 2 ** attempt;

      const cappedDelay = Math.min(
        exponentialDelay,
        maxDelay.total("milliseconds"),
      );

      // Add jitter to avoid thundering herd: delay ± (delay * jitterFactor)
      const jitter = cappedDelay * jitterFactor * (2 * Math.random() - 1);
      const finalDelay = Math.max(0, cappedDelay + jitter);
      const finalDelayInteger = Math.floor(finalDelay);

      await sleep(Temporal.Duration.from({ milliseconds: finalDelayInteger }));
    }
  }
  return await fn();
}
